import { layers, namedFlavor } from '@protomaps/basemaps';
import ipRegex from 'ip-regex';
import isCidr from 'is-cidr';
import { MapLibreMap, NavigationControl, setWorkerUrl } from 'maplibre-gl';

import GeoIpClient, {
  ErrorResp,
  UNHANDLED_ERROR_CODE,
  UNHANDLED_ERROR_MESSAGE,
} from '../geoip-client';
import {
  getCachedResponse,
  setCachedResponse,
} from '../geoip-client/demo-cache';
import { GeoIpDemoResponse } from '../geoip-client/types';
import { requireEl } from '../main/utils';
import { highlightJson } from './highlight-json';
import {
  IP_ADDRESS_NOT_FOUND_CODE,
  notFoundMessageNode,
} from './ip-address-not-found';

// TileJSON for the self-hosted Protomaps basemap.
const BASEMAP_TILEJSON = 'https://maptiles.maxmind.com/basemap.json';

// Bundling breaks MapLibre's own worker URL resolution. import.meta.url keeps
// the hash-prefixed static asset base this script was served from.
setWorkerUrl(new URL('../maplibre-gl-worker.js', import.meta.url).href);

// Inline UI feedback before submit. GeoIpClient runs its own checks per IP
// when the request is actually issued.
function validateIp(value: string): string | null {
  if (!value) {
    return null;
  }
  if (isCidr(value) !== 0) {
    return `The value '${value}' is in CIDR format and not a valid IP address.`;
  }
  if (!ipRegex({ exact: true }).test(value)) {
    return `The value '${value}' is not a valid IP address.`;
  }
  return null;
}

// All DOM elements the script depends on. Required elements throw if missing;
// `loading` is the only genuinely optional one (the template only renders it
// when an IP is present in the URL).
function cacheElements() {
  const ipDemoContainer = requireEl<HTMLElement>('[data-ip]');
  const searchForm = requireEl<HTMLFormElement>('.ip-demo__search-form');

  return {
    ipDemoContainer,
    ipDemoBaseUrl: ipDemoContainer.dataset.ipDemoBaseUrl ?? '',
    searchForm,
    searchInput: requireEl<HTMLInputElement>(
      '.ip-demo__search-input',
      searchForm,
    ),
    searchError: requireEl<HTMLElement>('.ip-demo__search-error', searchForm),
    csrfInput: requireEl<HTMLInputElement>('[name="csrf_token"]', searchForm),
    ipHeading: requireEl('.js-ip-demo-ip'),
    copyIpBtn: requireEl('.js-ip-demo-copy-ip'),
    shareBtn: requireEl('.js-ip-demo-share'),
    locationBadge: requireEl('.js-ip-demo-location-badge'),
    locationBadgeText: requireEl('.js-ip-demo-location-badge-text'),
    mapCard: requireEl('.js-ip-demo-map-card'),
    map: requireEl<HTMLElement>('.js-ip-demo-map'),
    mapCaption: requireEl('.js-ip-demo-map-caption'),
    mapCorrectionFallback: requireEl('.js-ip-demo-map-correction-fallback'),
    accuracyLearnMore: requireEl('.js-ip-demo-accuracy-learn-more'),
    jsonSection: requireEl('.js-ip-demo-json-section'),
    jsonCode: requireEl('.js-ip-demo-json-code'),
    copyJsonBtn: requireEl('.js-ip-demo-copy-json'),
    errorSection: requireEl('.js-ip-demo-error'),
    errorMessage: requireEl('.js-ip-demo-error-message'),
    results: requireEl('.js-ip-demo-results'),
    valueCells: document.querySelectorAll<HTMLElement>(
      '.js-ip-demo-value[data-field]',
    ),
    loading: document.querySelector('.js-ip-demo-loading'),
  };
}

const elements = cacheElements();

const setSearchError = (message: string | null) => {
  elements.searchError.textContent = message ?? '';
  elements.searchInput.classList.toggle('is-invalid', message !== null);
};

elements.searchForm.addEventListener('submit', (e) => {
  e.preventDefault();
  const ip = elements.searchInput.value.trim();

  if (!ip) {
    setSearchError('Please enter an IP address.');
    return;
  }

  const error = validateIp(ip);
  if (error) {
    setSearchError(error);
    return;
  }

  setSearchError(null);
  window.location.href = `${elements.ipDemoBaseUrl}/${ip}`;
});

elements.searchInput.addEventListener('input', () => setSearchError(null));

// Copy button helper. The `data-copying` attribute short-circuits re-entry
// while a previous click's restore timeout is still pending — without it,
// a second click would snapshot the temporary "Copied" state and re-apply
// it when its own timeout fires, leaving the button stuck.
function setupCopyButton(button: Element, text: string) {
  button.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(text);

      if (button.getAttribute('data-copying') === 'true') {
        return;
      }
      button.setAttribute('data-copying', 'true');

      const previousAriaLabel = button.getAttribute('aria-label');
      button.setAttribute('aria-label', 'Copied');

      const label = button.querySelector('.ip-demo__copy-label');
      const previousVisibleLabel = label?.textContent ?? null;
      if (label) label.textContent = 'Copied';

      const icon = button.querySelector('i');
      const previousIconClass = icon?.className ?? '';
      if (icon) {
        icon.className = 'bi bi-check-lg text-green';
      }

      setTimeout(() => {
        button.removeAttribute('data-copying');
        if (previousAriaLabel === null) {
          button.removeAttribute('aria-label');
        } else {
          button.setAttribute('aria-label', previousAriaLabel);
        }
        if (label && previousVisibleLabel !== null) {
          label.textContent = previousVisibleLabel;
        }
        if (icon) {
          icon.className = previousIconClass;
        }
      }, 1000);
    } catch {
      // Copy failed, don't change icon
    }
  });
}

function initMap(
  element: HTMLElement,
  lat: number,
  lng: number,
  accuracyRadiusKm: number,
): boolean {
  // Self-hosted glyph and sprite assets (vendored copy of
  // protomaps/basemaps-assets under static/basemaps-assets/). The host
  // attribute carries the hash-prefixed static asset base URL so
  // MapLibre's requests survive cache-busting on each deploy.
  const assetsBase = element.dataset.basemapsAssets;
  if (!assetsBase) {
    return false;
  }

  // MapLibre reports a missing WebGL2 context on the map's error event, which
  // fires from the constructor before a listener can attach.
  if (!document.createElement('canvas').getContext('webgl2')) {
    return false;
  }

  const sourceName = 'protomaps';
  const map = new MapLibreMap({
    container: element,
    attributionControl: { compact: false },
    style: {
      version: 8,
      glyphs: `${assetsBase}/fonts/{fontstack}/{range}.pbf`,
      sprite: `${assetsBase}/sprites/v4/light`,
      sources: {
        [sourceName]: { type: 'vector', url: BASEMAP_TILEJSON },
      },
      layers: layers(sourceName, namedFlavor('light'), { lang: 'en' }),
    },
    center: [lng, lat],
    zoom: 10,
  });

  map.addControl(new NavigationControl({ showCompass: false }), 'top-right');

  // MapLibre tracks window resize but not container resize. Debounce because
  // map.resize() reallocates the WebGL framebuffer, which flickers when
  // called on every layout tick during a drag-resize.
  let resizeTimer: ReturnType<typeof setTimeout> | undefined;
  new ResizeObserver(() => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => map.resize(), 100);
  }).observe(element);

  if (accuracyRadiusKm > 0) {
    map.on('load', () => addAccuracyCircle(map, lat, lng, accuracyRadiusKm));
  }

  return true;
}

function addAccuracyCircle(
  map: MapLibreMap,
  lat: number,
  lng: number,
  radiusKm: number,
) {
  const radiusM = radiusKm * 1000;
  const cosLat = Math.cos((lat * Math.PI) / 180);

  // Web Mercator: metersPerPixel(z, lat) = 156543.03 * cos(lat) / 2^z, so
  // pixelRadius(z) = pixelRadius(0) * 2^z. We interpolate exponentially from
  // zoom 0 to MapLibre's max zoom of 22.
  const pixelRadiusAtZoom0 = radiusM / (156543.03392 * cosLat);

  map.addSource('accuracy', {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lng, lat] },
      properties: {},
    },
  });

  map.addLayer({
    id: 'accuracy-circle',
    type: 'circle',
    source: 'accuracy',
    paint: {
      // $color-light-blue from web/main/css/marketing/variables.scss
      'circle-color': '#7abae0',
      'circle-opacity': 0.15,
      'circle-stroke-color': '#7abae0',
      'circle-stroke-width': 2,
      'circle-radius': [
        'interpolate',
        ['exponential', 2],
        ['zoom'],
        0,
        pixelRadiusAtZoom0,
        22,
        pixelRadiusAtZoom0 * 2 ** 22,
      ],
    },
  });

  const earthCircumference = 40075016.686;
  const latDelta = (radiusM * 360) / earthCircumference;
  const lngDelta = latDelta / cosLat;
  map.fitBounds(
    [
      [lng - lngDelta, lat - latDelta],
      [lng + lngDelta, lat + latDelta],
    ],
    { padding: 12, animate: false },
  );
  map.zoomTo(map.getZoom() + 1, { animate: false });
}

// Field mapping from API response to data-field attributes
function mapResponseToFields(
  response: GeoIpDemoResponse,
): Record<string, string> {
  const country = response.country?.names?.en;
  const countryCode = response.country?.iso_code;
  const countryDisplay = country
    ? countryCode
      ? `${country} (${countryCode})`
      : country
    : '-';

  const lat = response.location?.latitude;
  const lng = response.location?.longitude;
  const coordinates =
    lat !== undefined && lng !== undefined ? `${lat}, ${lng}` : '-';

  const accuracyRadius = response.location?.accuracy_radius;
  const accuracyDisplay =
    accuracyRadius !== undefined ? `${accuracyRadius} km` : '-';

  const asn = response.traits?.autonomous_system_number;
  const asnDisplay = asn !== undefined ? `AS${asn}` : '-';

  return {
    city: response.city?.names?.en ?? '-',
    subdivision: response.subdivisions?.[0]?.names?.en ?? '-',
    subdivision_code: response.subdivisions?.[0]?.iso_code ?? '-',
    country: countryDisplay,
    postal_code: response.postal?.code ?? '-',
    continent: response.continent?.names?.en ?? '-',
    is_in_eu: (response.country?.is_in_european_union ?? false).toString(),
    represented_country_type: response.represented_country?.type ?? '-',
    timezone: response.location?.time_zone ?? '-',
    coordinates,
    accuracy_radius: accuracyDisplay,
    as_number: asnDisplay,
    as_org: response.traits?.autonomous_system_organization ?? '-',
    network: response.traits?.network ?? '-',
    isp: response.traits?.isp ?? '-',
    organization: response.traits?.organization ?? '-',
    domain: response.traits?.domain ?? '-',
    connection_type: response.traits?.connection_type ?? '-',
    mobile_country_code: response.traits?.mobile_country_code ?? '-',
    mobile_network_code: response.traits?.mobile_network_code ?? '-',
    is_anycast: response.traits?.is_anycast?.toString() ?? '-',
  };
}

function buildLocationBadge(response: GeoIpDemoResponse): string | null {
  const parts = [
    response.city?.names?.en,
    response.subdivisions?.[0]?.names?.en,
    response.country?.names?.en,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
}

function populateResults(response: GeoIpDemoResponse) {
  const fields = mapResponseToFields(response);

  elements.valueCells.forEach((cell) => {
    const field = cell.dataset.field;
    if (field && field in fields) {
      cell.textContent = fields[field];
    }
  });

  const ip = response.traits?.ip_address ?? '';
  elements.ipHeading.textContent = ip;
  if (ip) {
    setupCopyButton(elements.copyIpBtn, ip);
    const permalink = `${window.location.origin}${elements.ipDemoBaseUrl}/${ip}`;
    setupCopyButton(elements.shareBtn, permalink);
  }

  const badge = buildLocationBadge(response);
  if (badge) {
    elements.locationBadgeText.textContent = badge;
    elements.locationBadge.classList.remove('d-none');
  }

  const lat = response.location?.latitude;
  const lng = response.location?.longitude;
  const accuracy = response.location?.accuracy_radius ?? 0;

  // The map is non-essential; a failure to initialize it must not block the
  // rest of the results from rendering.
  let mapInitialized = false;
  if (lat !== undefined && lng !== undefined) {
    try {
      mapInitialized = initMap(elements.map, lat, lng, accuracy);
    } catch (error) {
      console.error('Map initialization failed', error);
    }
  }

  if (mapInitialized) {
    if (accuracy > 0) {
      elements.mapCaption.textContent = `Approximate location – accuracy radius ${accuracy} km`;
    }
    if (accuracy > 100) {
      elements.accuracyLearnMore.classList.remove('d-none');
    }
  } else {
    elements.mapCard.classList.add('d-none');
    elements.mapCorrectionFallback.classList.remove('d-none');
  }

  const jsonStr = JSON.stringify(response, null, 2);
  elements.jsonCode.replaceChildren(highlightJson(jsonStr));
  elements.jsonSection.classList.remove('d-none');
  setupCopyButton(elements.copyJsonBtn, jsonStr);
}

function showError(message: string | Node) {
  elements.loading?.remove();
  elements.errorMessage.replaceChildren(message);
  elements.errorSection.classList.remove('d-none');
}

function showResults() {
  elements.loading?.remove();
  elements.results.classList.remove('d-none');
}

// Fetch and display IP data
const initIPDemo = async () => {
  const ip = elements.ipDemoContainer.dataset.ip;
  if (!ip) return;

  const cached = getCachedResponse(ip, 'city');
  if (cached) {
    populateResults(cached.data as GeoIpDemoResponse);
    showResults();
    return;
  }

  const csrfToken = elements.csrfInput.value;
  if (!csrfToken) return;

  const client = new GeoIpClient({
    csrfToken,
    isDemo: true,
  });

  try {
    const results = await client.lookupIpAddresses<GeoIpDemoResponse>(
      [ip],
      'city',
    );
    const result = results[0];

    if ('error' in result) {
      const err = result as ErrorResp;
      if (err.code === IP_ADDRESS_NOT_FOUND_CODE) {
        showError(notFoundMessageNode());
      } else if (err.code === UNHANDLED_ERROR_CODE) {
        showError(UNHANDLED_ERROR_MESSAGE);
      } else {
        showError(err.error);
      }
      return;
    }

    const cityResult = result as GeoIpDemoResponse;
    setCachedResponse(cityResult.traits?.ip_address ?? ip, cityResult, 'city');
    populateResults(cityResult);
    showResults();
  } catch {
    showError(UNHANDLED_ERROR_MESSAGE);
  }
};

initIPDemo();
